/**
 * The market the app is being used in: Japan (app.callout.jp) or the US
 * (app.heycallout.com). One build serves both hosts; everything that differs
 * between them (copy, links, the default country to call, the Stripe pricing
 * table) is looked up here from the hostname.
 *
 * The backend has the same notion in markets.py and records a call's market
 * from the number it goes to, not from the host, so a US user calling Japan
 * is billed and reported like any other call to Japan.
 */

export type MarketId = 'jp' | 'us';
export type CountryCode = 'JP' | 'US';

export interface Market {
  id: MarketId;
  /** Public site, e.g. https://callout.jp */
  siteUrl: string;
  appUrl: string;
  supportEmail: string;
  termsUrl: string;
  privacyUrl: string;
  /** Country preselected in the phone number field. */
  defaultCountry: CountryCode;
  defaultLanguage: 'ja' | 'en';
  /** Shown on the sign-in page under the brand. */
  tagline: string[];
  /** Stripe pricing table for this market's currency. */
  pricingTableId: string;
  /**
   * Price of one extra call, as shown: `subscriber` on a plan (bought on the
   * Plans page), `standard` without one (the pricing table's one-off call).
   * The charged prices are set in Stripe; see payment_backend/README.md.
   */
  extraCallPrice: { subscriber: string; standard: string };
  /** An example callback number for the prompt example, in local format. */
  exampleCallbackNumber: string;
}

/**
 * Stripe pricing tables: yen for callout.jp, USD for heycallout.com (the
 * "Callout 10/20/50/One" products with USD prices).
 */
const PRICING_TABLE_JPY = 'prctbl_1SzfBmHjnBdpOfn3Ga3A4oYj';
const PRICING_TABLE_USD = 'prctbl_1UTqkqHjnBdpOfn3GIGnsKhK';

export const MARKETS: Record<MarketId, Market> = {
  jp: {
    id: 'jp',
    siteUrl: 'https://callout.jp',
    appUrl: 'https://app.callout.jp',
    supportEmail: 'hello@callout.jp',
    termsUrl: 'https://callout.jp/terms-en',
    privacyUrl: 'https://callout.jp/privacy-en',
    defaultCountry: 'JP',
    defaultLanguage: 'ja',
    tagline: ['AI phone calls in Japan', 'from the browser', 'in Japanese or English'],
    pricingTableId: PRICING_TABLE_JPY,
    extraCallPrice: { subscriber: '¥150', standard: '¥200' },
    exampleCallbackNumber: '090-1234-5678',
  },
  us: {
    id: 'us',
    siteUrl: 'https://heycallout.com',
    appUrl: 'https://app.heycallout.com',
    supportEmail: 'hello@heycallout.com',
    termsUrl: 'https://heycallout.com/terms',
    privacyUrl: 'https://heycallout.com/privacy',
    defaultCountry: 'US',
    defaultLanguage: 'en',
    tagline: ['AI phone calls in the US and Canada', 'from the browser', 'in English or Japanese'],
    pricingTableId: PRICING_TABLE_USD,
    extraCallPrice: { subscriber: '$1.50', standard: '$2' },
    exampleCallbackNumber: '(415) 555-0142',
  },
};

/** The market for a hostname: heycallout.com and its subdomains are the US. */
export function marketForHost(hostname: string): Market {
  const host = hostname.toLowerCase();
  if (host === 'heycallout.com' || host.endsWith('.heycallout.com')) return MARKETS.us;
  return MARKETS.jp;
}

/**
 * The current market. On localhost and preview hosts it is Japan unless the
 * build sets REACT_APP_MARKET=us, which is how the US app is developed locally.
 */
export function currentMarket(): Market {
  const forced = process.env.REACT_APP_MARKET;
  if (forced === 'us' || forced === 'jp') {
    const host = window.location.hostname;
    const isProduction = host.endsWith('callout.jp') || host.endsWith('heycallout.com');
    if (!isProduction) return MARKETS[forced];
  }
  return marketForHost(window.location.hostname);
}
